import { FormGroup, Input, Label } from '@southfields-digital/mxmz-react-ui';

import { SoccerMatchControl } from 'src/types/matchControl';
import { Clock as ClockType } from 'src/types/matchControl/clock';
import { Contestant as ContestantType } from 'src/types/matchControl/contestant';

import Clock from './Clock';
import Contestant from './Contestant';
import styles from './RundownMatchControl.module.scss';
import { ChangeEvent } from 'react';

type Props = {
  handleClickMedia: (contestant: ContestantType, index: number) => void;
  handleChangeContestant: (contestant: ContestantType, index: number) => void;
  handleChangeClock: (clock: ClockType, index: number) => void;
  contestants: ContestantType[];
  clocks: ClockType[];
  penaltyClocks: SoccerMatchControl['penaltyClocks'];
  handleChangePenaltyClock: (config: SoccerMatchControl['penaltyClocks']) => void;
};

const Soccer = ({
  clocks,
  contestants,
  penaltyClocks,
  handleChangeContestant,
  handleChangeClock,
  handleClickMedia,
  handleChangePenaltyClock,
}: Props) => {
  const penaltyClockEnabled = penaltyClocks?.enabled ?? false;
  const penaltyClockTime = penaltyClocks?.time ?? 0;

  const handleChangePenaltyClockEnabled = () => {
    handleChangePenaltyClock({
      ...penaltyClocks,
      enabled: !penaltyClockEnabled,
      time: penaltyClockTime,
    });
  };

  const penaltyClockMinutes = Math.floor(penaltyClockTime / 60000);
  const penaltyClockSeconds = Math.floor((penaltyClockTime % 60000) / 1000);

  const handleChangePenaltyClockTime = (
    e: React.ChangeEvent<HTMLInputElement>,
    value: 'seconds' | 'minutes'
  ) => {
    const time = parseInt(e.currentTarget.value.replace(/[^0-9.]/g, ''));
    if (isNaN(time)) return;

    const newPenaltyClockTime =
      value === 'seconds'
        ? (penaltyClockMinutes || 0) * 60000 + time * 1000
        : time * 60000 + (penaltyClockSeconds || 0) * 1000;

    if (newPenaltyClockTime < 0) return;

    handleChangePenaltyClock({
      ...penaltyClocks,
      time: newPenaltyClockTime,
    });
  };

  return (
    <div className={styles.SportContainer}>
      <div className={styles.Contestants}>
        {contestants?.map((contestant, idx) => (
          <Contestant
            key={idx}
            sport="soccer"
            contestant={contestant}
            handleChangeContestant={(contestant) => handleChangeContestant(contestant, idx)}
            handleClickMedia={(contestant) => handleClickMedia(contestant, idx)}
            inverted={idx % 2 === 1}
          />
        ))}
      </div>
      <div className={styles.Clocks}>
        {clocks?.map((clock, idx) => (
          <div key={idx}>
            <Clock clock={clock} handleChangeClock={(clock) => handleChangeClock(clock, idx)} />
          </div>
        ))}
      </div>
      <div className="w-full flex flex-col gap-2">
        <div className="flex flex-row gap-2">
          <Label className="w-full align-middle !m-0">Penalty clock</Label>
          <Input
            size="sm"
            type="toggle"
            checked={penaltyClockEnabled}
            onChange={handleChangePenaltyClockEnabled}
          />
        </div>
        {penaltyClockEnabled && (
          <div className="flex flex-row gap-2 w-full">
            <FormGroup label="[min] Penalty clock" htmlFor="penaltyClockMin">
              <Input
                type="number"
                id="penaltyClockMin"
                value={penaltyClockMinutes}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleChangePenaltyClockTime(e, 'minutes')
                }
              />
            </FormGroup>
            <FormGroup label="[sec] Penalty clock" htmlFor="penaltyClockSec">
              <Input
                type="number"
                id="penaltyClockSec"
                maxLength={2}
                value={penaltyClockSeconds}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleChangePenaltyClockTime(e, 'seconds')
                }
              />
            </FormGroup>
          </div>
        )}
      </div>
    </div>
  );
};

export default Soccer;
